import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axiosClient from "../features/axiosInstance";
import { useAccessControl } from "../context/AccessControlContext";

function AdminTaskList() {
  const theme = useTheme();
  const { isAdmin, isLoading } = useAccessControl();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);

  // Fetch tasks on mount
  useEffect(() => {
    if (isAdmin) {
      fetchTasks();
      // Auto-refresh every 5 seconds
      const interval = setInterval(fetchTasks, 5000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const fetchTasks = async () => {
    try {
      setError(null);
      const response = await axiosClient.get("/admin/recalculation/tasks");
      setTasks(response.data.tasks || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (jobId) => {
    try {
      await axiosClient.post(`/admin/recalculation/${jobId}/cancel`);
      fetchTasks(); // Refresh list
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleDownload = (task, csvKey) => {
    if (!task.data?.csvFiles?.[csvKey]) return;

    const csv = task.data.csvFiles[csvKey].content;
    const filename = task.data.csvFiles[csvKey].filename;

    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/csv;charset=utf-8," + encodeURIComponent(csv)
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getStatusChip = (task) => {
    const statusConfig = {
      pending: { label: "Pending", color: "default" },
      active: { label: "Running", color: "info" },
      completed: { label: "Completed", color: "success" },
      failed: { label: "Failed", color: "error" },
      cancelled: { label: "Cancelled", color: "warning" },
    };

    const config = statusConfig[task.state] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          Access Denied: Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Recalculation Tasks
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              setLoading(true);
              fetchTasks();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {tasks.length === 0 ? (
          <Card>
            <CardContent>
              <Typography color="textSecondary" align="center">
                No tasks yet. Start a recalculation from the Admin Actions page.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.primary.main }}>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Task ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Progress
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Completed
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Results
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks
                  .sort((a, b) => {
                    // Sort by finishedOn descending (most recent first)
                    const aDate = a.finishedOn
                      ? new Date(a.finishedOn).getTime()
                      : 0;
                    const bDate = b.finishedOn
                      ? new Date(b.finishedOn).getTime()
                      : 0;
                    return bDate - aDate;
                  })
                  .map((task, index) => (
                    <TableRow
                      key={task.id}
                      hover
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? index % 2 === 0
                              ? theme.palette.background.paper
                              : theme.palette.grey[800]
                            : index % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.grey[100],
                        "&:hover": { bgcolor: theme.palette.action.hover },
                      }}
                    >
                      <TableCell
                        sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                      >
                        {task.id.substring(0, 12)}...
                      </TableCell>
                      <TableCell>
                        {task.isDryRun ? "Dry Run" : "Execution"}
                      </TableCell>
                      <TableCell>{getStatusChip(task)}</TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={task.status || 0}
                            size={30}
                          />
                          <Typography variant="caption">
                            {task.status || 0}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDate(task.finishedOn || task.createdAt)}
                      </TableCell>
                      <TableCell>
                        {task.state === "completed" && task.data?.csvFiles ? (
                          <Stack direction="row" spacing={0.5}>
                            {task.data.csvFiles.entries && (
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(task, "entries")}
                              >
                                Entries
                              </Button>
                            )}
                            {task.data.csvFiles.rankings && (
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(task, "rankings")}
                              >
                                Rankings
                              </Button>
                            )}
                          </Stack>
                        ) : task.state === "completed" ? (
                          <Typography variant="caption" color="textSecondary">
                            No files
                          </Typography>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedTask(task);
                            setDetailsDialog(true);
                          }}
                          title="View details"
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                        {(task.state === "active" ||
                          task.state === "pending") && (
                          <IconButton
                            size="small"
                            onClick={() => handleCancel(task.id)}
                            color="error"
                            title="Cancel task"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Task Details Dialog */}
      {selectedTask && (
        <Dialog
          open={detailsDialog}
          onClose={() => setDetailsDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Task Details</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Task ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {selectedTask.id}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Type
                </Typography>
                <Typography variant="body2">
                  {selectedTask.isDryRun ? "Dry Run" : "Execution"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Status
                </Typography>
                <Typography variant="body2">
                  {getStatusChip(selectedTask)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Progress
                </Typography>
                <Typography variant="body2">
                  {selectedTask.status || 0}%
                </Typography>
              </Box>

              {selectedTask.data && (
                <>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Statistics
                    </Typography>
                    <Typography variant="body2">
                      Total: {selectedTask.data.total}, Updated:{" "}
                      {selectedTask.data.updated}, Errors:{" "}
                      {selectedTask.data.errors}
                    </Typography>
                  </Box>
                </>
              )}

              <Box>
                <Typography variant="caption" color="textSecondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatDate(selectedTask.createdAt)}
                </Typography>
              </Box>

              {selectedTask.finishedOn && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(selectedTask.finishedOn)}
                  </Typography>
                </Box>
              )}

              {selectedTask.failedReason && (
                <Alert severity="error">{selectedTask.failedReason}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default AdminTaskList;
